import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatNumber, tooltipStyle } from '../types';

interface LeaderboardTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = ['leaderboard-dashboard'];

const LeaderboardTab: React.FC<LeaderboardTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const lb = data['leaderboard-dashboard'] || {};
  const funnel = lb.funnel || [];
  const taskEng = lb.taskEngagement || [];
  const coinRev = lb.coinRevenue || [];
  const notifSt = lb.notifStats || [];
  const bannerP = lb.bannerPerf || [];
  const badgeD = lb.badgeDist || [];
  const trends = lb.lbDailyTrends || [];
  const actions = lb.actionsBreakdown || [];
  const prefs = lb.notifPrefs || {};

  // Build funnel stages
  const funnelStages = [
    { name: 'Joined', users: funnel.find((f: any) => f.event === 'journey_leaderboard_join')?.uniqueUsers || 0 },
    { name: 'Viewed Tasks', users: funnel.find((f: any) => f.event === 'tasks_view')?.uniqueUsers || 0 },
    { name: 'Viewed Leaderboard', users: funnel.find((f: any) => f.event === 'leaderboard_view')?.uniqueUsers || 0 },
    { name: 'Moved to Wallet', users: funnel.find((f: any) => f.event === 'leaderboard_action' && f.action === 'slide_to_wallet_complete')?.uniqueUsers || 0 },
    { name: 'Bought Coins', users: funnel.find((f: any) => f.event === 'coin_purchase_flow' && f.action === 'purchase_success')?.uniqueUsers || 0 },
  ];

  // Banner performance
  const bannerTypes = ['move_to_wallet', 'buy_coins_tier', 'plus_upsell', 'notification_enable'];
  const bannerData = bannerTypes.map(bt => ({
    name: bt.replace(/_/g, ' '),
    impressions: bannerP.find((b: any) => b.bannerType === bt && b.action === 'impression')?.count || 0,
    clicks: bannerP.find((b: any) => b.bannerType === bt && b.action === 'click')?.count || 0,
  }));

  // Notification effectiveness
  const notifTypes = ['overtake', 'daily_summary', 'badge_award', 'weekly'];
  const notifData = notifTypes.map(nt => ({
    name: nt.replace(/_/g, ' '),
    sent: notifSt.find((n: any) => n.notifType === nt && n.action === 'sent')?.count || 0,
    clicked: notifSt.find((n: any) => n.notifType === nt && (n.action === 'deeplink_click' || n.action === 'clicked'))?.count || 0,
    uniqueUsers: notifSt.find((n: any) => n.notifType === nt && (n.action === 'deeplink_click' || n.action === 'clicked'))?.uniqueUsers || 0,
  }));

  // Task completion
  const taskCompleted = taskEng.filter((t: any) => t.action === 'completed').sort((a: any, b: any) => b.count - a.count);
  const taskStarted = taskEng.filter((t: any) => t.action === 'started');

  // Compute totals for stat cards
  const totalJoined = funnelStages[0].users;
  const totalPurchases = coinRev.reduce((s: number, d: any) => s + d.purchases, 0);
  const totalRevenue = coinRev.reduce((s: number, d: any) => s + d.totalRevenue, 0);
  const totalBannerClicks = bannerData.reduce((s: number, d: any) => s + d.clicks, 0);

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Users Joined" value={totalJoined} subtitle="30 days" />
        <StatCard title="Coin Purchases" value={totalPurchases} subtitle="30 days" />
        <StatCard title="Coin Revenue" value={`$${totalRevenue.toFixed(2)}`} subtitle="30 days" />
        <StatCard title="Banner Clicks" value={totalBannerClicks} subtitle="30 days" />
      </div>

      {/* Funnel */}
      <ChartCard title="Leaderboard Funnel (30d)">
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={funnelStages} layout="vertical">
            <XAxis type="number" stroke="#666" />
            <YAxis type="category" dataKey="name" width={120} stroke="#999" tick={{ fill: '#999', fontSize: 12 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="users" fill="#FF9C15" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Banner Performance + Task Engagement */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Smart Banner Performance">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={bannerData}>
              <XAxis dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
              <YAxis stroke="#666" />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="impressions" fill="#FF9C15" name="Impressions" />
              <Bar dataKey="clicks" fill="#F42026" name="Clicks" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Tasks Completed">
          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {taskCompleted.slice(0, 10).map((t: any, i: number) => {
              const started = taskStarted.find((s: any) => s.taskType === t.taskType)?.count || t.count;
              const rate = started > 0 ? ((t.count / started) * 100).toFixed(0) : '0';
              return (
                <div key={i} className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-sm text-gray-300">{t.taskType || 'unknown'}</span>
                  <div className="flex gap-3">
                    <span className="text-xs text-gray-500">{formatNumber(t.count)} done</span>
                    <span className="text-xs font-medium text-emerald-400">{rate}%</span>
                  </div>
                </div>
              );
            })}
            {taskCompleted.length === 0 && <p className="text-gray-500 text-sm">No task data yet</p>}
          </div>
        </ChartCard>
      </div>

      {/* Coin Revenue + Notification Effectiveness */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Coin Purchase Revenue (Daily)">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={coinRev}>
              <defs>
                <linearGradient id="lbRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF9C15" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#FF9C15" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
              <YAxis stroke="#666" />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="totalRevenue" stroke="#FF9C15" fill="url(#lbRevGrad)" name="Revenue ($)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Notification Effectiveness">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={notifData}>
              <XAxis dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
              <YAxis stroke="#666" />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="sent" fill="#FF9C15" name="Sent" />
              <Bar dataKey="clicked" fill="#34d399" name="Clicked" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Badge Distribution + Notification Prefs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChartCard title="Badge Distribution">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={badgeD}>
              <XAxis dataKey="name" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
              <YAxis stroke="#666" />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="totalEarned" fill="#FF9C15" radius={[6, 6, 0, 0]} name="Total Earned" />
              <Bar dataKey="uniqueUsers" fill="#38bdf8" radius={[6, 6, 0, 0]} name="Unique Users" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Notification Preferences">
          <div className="space-y-4 pt-4">
            <div className="flex justify-between"><span className="text-gray-400">Total Users with Prefs</span><span className="text-white font-bold">{formatNumber(prefs.totalUsers || 0)}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Overtake Alerts Enabled</span><span className="text-emerald-400 font-bold">{formatNumber(prefs.overtakeEnabled || 0)}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Daily Summary Enabled</span><span className="text-emerald-400 font-bold">{formatNumber(prefs.dailyEnabled || 0)}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Weekly Results Enabled</span><span className="text-emerald-400 font-bold">{formatNumber(prefs.weeklyEnabled || 0)}</span></div>
          </div>
        </ChartCard>
      </div>

      {/* Actions Breakdown */}
      <ChartCard title="Leaderboard Actions (Last 7 Days)">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {actions.map((a: any, i: number) => (
            <div key={i} className="bg-white/5 rounded-xl p-3">
              <p className="text-xs text-gray-500">{(a.action || '').replace(/_/g, ' ')}</p>
              <p className="text-lg font-bold text-white">{formatNumber(a.count)}</p>
              <p className="text-xs text-gray-500">{formatNumber(a.uniqueUsers)} users</p>
            </div>
          ))}
          {actions.length === 0 && <p className="text-gray-500 text-sm col-span-4">No action data yet</p>}
        </div>
      </ChartCard>

      {/* Daily Trends */}
      <ChartCard title="Daily Trends (30d)">
        {trends.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={(() => {
              const days = Array.from(new Set(trends.map((t: any) => t.day))).sort();
              return days.map(day => {
                const row: any = { day };
                trends.filter((t: any) => t.day === day).forEach((t: any) => {
                  row[t.event] = t.count;
                });
                return row;
              });
            })()}>
              <XAxis dataKey="day" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
              <YAxis stroke="#666" />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="tasks_view" stroke="#FF9C15" name="Tasks View" dot={false} />
              <Line type="monotone" dataKey="leaderboard_view" stroke="#F42026" name="Leaderboard View" dot={false} />
              <Line type="monotone" dataKey="coin_purchase_flow" stroke="#34d399" name="Coin Purchases" dot={false} />
              <Line type="monotone" dataKey="smart_banner_event" stroke="#38bdf8" name="Banner Events" dot={false} />
              <Line type="monotone" dataKey="lb_notification_event" stroke="#c084fc" name="Notifications" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : <p className="text-gray-500 text-sm">No trend data yet</p>}
      </ChartCard>

      <p className="text-xs text-gray-600 text-right">Updated: {lb.updatedAt ? new Date(lb.updatedAt).toLocaleString() : 'N/A'}</p>
    </div>
  );
};

export default LeaderboardTab;
